import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { BUSINESS, waLink } from "@/lib/config";
import { toast } from "sonner";

const PRODUCT_OPTIONS = [
  "Modular Switches",
  "Wires & Cables",
  "LED Lighting",
  "Fans",
  "PVC Conduits",
  "Switchgear",
  "Geysers",
  "Electrical Accessories",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    product: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.product) {
      toast.error("Please fill Name, Mobile and Product Required");
      return;
    }
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    const text = `Hi Master Mall, I'd like to enquire.

*Name:* ${form.name}
*Mobile:* ${form.mobile}
*Product Required:* ${form.product}
*Message:* ${form.message || "-"}`;

    toast.success("Opening WhatsApp with your enquiry…");
    window.open(waLink(text), "_blank", "noopener,noreferrer");
  };

  const info = [
    {
      Icon: MapPin,
      label: "Visit Us",
      value: (
        <>
          {BUSINESS.address.line1}
          <br />
          {BUSINESS.address.line2}
          <br />
          {BUSINESS.address.line3}
        </>
      ),
    },
    {
      Icon: Phone,
      label: "Call Us",
      value: (
        <a
          href={`tel:${BUSINESS.phoneRaw}`}
          className="hover:text-[#FFD500] transition-colors"
          data-testid="contact-phone-link"
        >
          {BUSINESS.phoneDisplay}
        </a>
      ),
    },
    {
      Icon: Mail,
      label: "Email Us",
      value: (
        <a
          href={`mailto:${BUSINESS.email}`}
          className="hover:text-[#FFD500] transition-colors break-all"
          data-testid="contact-email-link"
        >
          {BUSINESS.email}
        </a>
      ),
    },
    {
      Icon: Clock,
      label: "Working Hours",
      value: (
        <>
          {BUSINESS.hours.map((h) => (
            <div key={h.day}>
              <span className="text-[#334155]">{h.day}:</span>{" "}
              <span className="font-semibold text-[#00296B]">{h.time}</span>
            </div>
          ))}
        </>
      ),
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#334155] font-semibold mb-4">
            — Get in Touch
          </div>
          <h2
            className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#00296B] leading-tight mm-underline-center"
            data-testid="contact-heading"
          >
            Let&apos;s power your next project
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#334155] max-w-2xl mx-auto">
            Visit our showroom, drop a message on WhatsApp or fill the form —
            we&apos;ll get back within business hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Info + Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 space-y-4"
          >
            {info.map((it, i) => (
              <div
                key={i}
                data-testid={`contact-info-${i}`}
                className="flex gap-4 p-5 bg-[#F8F9FA] border border-[#00296B]/10 hover:border-[#FFD500] transition-colors"
              >
                <div className="w-11 h-11 bg-[#00296B] flex-shrink-0 flex items-center justify-center">
                  <it.Icon className="w-5 h-5 text-[#FFD500]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#334155] font-semibold">
                    {it.label}
                  </div>
                  <div className="mt-1 text-[#00296B] text-sm sm:text-base leading-relaxed">
                    {it.value}
                  </div>
                </div>
              </div>
            ))}

            <div
              className="aspect-[4/3] w-full border border-[#00296B]/10 overflow-hidden"
              data-testid="contact-map"
            >
              <iframe
                title="Master Mall Nashik Location"
                src={BUSINESS.mapEmbedSrc}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 bg-[#00296B] p-8 sm:p-10 lg:p-12 relative"
          >
            <div className="absolute top-0 right-0 w-24 h-24 border-t-4 border-r-4 border-[#FFD500]" />
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-white leading-tight">
              Send an enquiry
            </h3>
            <p className="mt-2 text-white/70 text-sm">
              Your details go straight to our WhatsApp for the fastest response.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Field
                label="Your Name *"
                testId="form-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="e.g. Rahul Sharma"
              />
              <Field
                label="Mobile Number *"
                testId="form-mobile"
                value={form.mobile}
                onChange={(v) => update("mobile", v)}
                placeholder="e.g. 98765 43210"
                type="tel"
              />

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-[#FFD500] font-semibold mb-2">
                  Product Required *
                </label>
                <select
                  value={form.product}
                  onChange={(e) => update("product", e.target.value)}
                  data-testid="form-product"
                  className="w-full bg-transparent border-b-2 border-white/25 focus:border-[#FFD500] outline-none text-white py-3 transition-colors"
                >
                  <option value="" className="text-[#00296B]">
                    Select a category
                  </option>
                  {PRODUCT_OPTIONS.map((p) => (
                    <option key={p} value={p} className="text-[#00296B]">
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-[#FFD500] font-semibold mb-2">
                  Message
                </label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="form-message"
                  placeholder="Tell us about your requirement…"
                  className="w-full bg-transparent border-b-2 border-white/25 focus:border-[#FFD500] outline-none text-white py-3 placeholder:text-white/40 resize-none transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="form-submit"
              className="mt-8 group inline-flex items-center gap-3 bg-[#FFD500] text-[#00296B] font-semibold px-8 py-4 hover:bg-[#E6C000] transition-colors"
            >
              Send via WhatsApp
              <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#FFD500] font-semibold mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b-2 border-white/25 focus:border-[#FFD500] outline-none text-white py-3 placeholder:text-white/40 transition-colors"
      />
    </div>
  );
}
